export type NumberCallback = (num: number) => void
export type StatusCallback = (connected: boolean) => void

/** Number range to extract from barrage messages (1-100) */
const NUM_RE = /(?:^|[^\d])([1-9][0-9]?|100)(?!\d)/g

/**
 * WebSocket client that:
 * - Connects to ws://127.0.0.1:1088/ws/<roomId>
 * - Parses numbers from incoming barrage text
 * - Falls back to demo simulation when WS is unavailable
 */
export class WSClient {
  private ws: WebSocket | null = null
  private listening = false
  private demoTimer: ReturnType<typeof setInterval> | null = null
  private onNumber: NumberCallback | null = null

  connect(roomId: string, onNumber: NumberCallback, onStatus: StatusCallback): void {
    this.onNumber = onNumber
    this.disconnect()

    try {
      // 82.156.222.236:6003
      // 127.0.0.1:1088
      // wss://wss.live.douyin.nofuzz.cn
      this.ws = new WebSocket(`wss://wss.live.douyin.nofuzz.cn/ws/${roomId}`)

      this.ws.onopen = () => {
        console.info(`[WS] Connected → room "${roomId}"`)
        onStatus(true)
      }

      this.ws.onmessage = ({ data }) => {
        if (!this.listening) return

        let json: any
        try {
          json = JSON.parse(data)
        } catch {
          return
        }

        if (json?.common?.method !== 'WebcastChatMessage') return

        const content = json.content || ''
        console.log('弹幕内容:', content)

        const matches = content.matchAll(NUM_RE)

        for (const match of matches) {
          const n = Number(match[1])
          if (n >= 1 && n <= 100) {
            console.log(`%c中标:${content}, [${n}]`, 'color: blue;');
            this.onNumber?.(n)
          }
        }
      }

      this.ws.onclose = () => {
        console.info('[WS] Disconnected')
        onStatus(false)
      }

      this.ws.onerror = () => {
        console.warn('[WS] Could not connect — demo mode active')
        onStatus(false)
      }
    } catch (err) {
      console.warn('[WS] WebSocket init failed', err)
      onStatus(false)
    }
  }

  /** Start accepting messages (call when game begins) */
  startListening(): void {
    this.listening = true
  }

  /** Stop accepting messages (call when game ends) */
  stopListening(): void {
    this.listening = false
    this.stopDemo()
  }

  /** Kick off demo simulation (one random number every ~1.2s) */
  startDemo(rangeMin: number, rangeMax: number): void {
    this.stopDemo()
    this.demoTimer = setInterval(() => {
      if (!this.listening || !this.onNumber) return
      const pool = Array.from({ length: rangeMax - rangeMin + 1 }, (_, i) => i + rangeMin)
      const pick = pool[Math.floor(Math.random() * pool.length)]
      this.onNumber(pick)
    }, 1200)
  }

  stopDemo(): void {
    if (this.demoTimer) {
      clearInterval(this.demoTimer)
      this.demoTimer = null
    }
  }

  /** Update demo range when game range narrows */
  updateDemoRange(min: number, max: number): void {
    if (this.demoTimer !== null) this.startDemo(min, max)
  }

  disconnect(): void {
    this.stopListening()
    this.ws?.close()
    this.ws = null
  }
}
